'use client'

import React, { type ReactNode, useRef, useState } from 'react'

import { PlayIcon } from '../icons/Play'

import { cn } from './cn'

interface ClickToPlayVideoProps {
  /** Poster image shown while the video is inactive. */
  posterUrl?: string
  /** Native video URL (mp4/webm). Falls back to poster-only when absent. */
  videoUrl?: string
  /** Alt text for the poster and aria-label for the play button. */
  label?: string
  /** Extra classes applied to the outer wrapper (aspect ratio, corners, etc). */
  className?: string
  /** Extra classes applied to the poster <img> / native <video>. */
  mediaClassName?: string
  /** Overlay rendered on top of the poster (e.g. author gradient). Hidden while playing. */
  posterOverlay?: ReactNode
}

/**
 * Native <video> click-to-play/pause primitive mirroring the legacy Rails
 * `PartnerPage/SectionTestimonials.jsx` used by /travel PPP: a poster + play
 * overlay hand off to a native <video> that toggles play/pause on click and
 * resets to the poster onEnded. Shared by the S&P home ReviewFeed and the
 * How It Works videos section.
 */
export const ClickToPlayVideo = ({
  posterUrl,
  videoUrl,
  label,
  className,
  mediaClassName,
  posterOverlay,
}: ClickToPlayVideoProps): React.ReactElement => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlayClick = (): void => {
    if (!videoUrl) return
    setIsPlaying(true)
    setTimeout(() => {
      videoRef.current?.play()
    }, 0)
  }

  const handleVideoClick = (): void => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const handleVideoEnded = (): void => {
    setIsPlaying(false)
    if (videoRef.current) {
      videoRef.current.currentTime = 0
    }
  }

  const mediaClasses = cn('snplay:block snplay:w-full snplay:object-cover', mediaClassName)

  return (
    <div className={cn('snplay:relative snplay:overflow-hidden', className)}>
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          playsInline
          onClick={handleVideoClick}
          onEnded={handleVideoEnded}
          className={cn(mediaClasses, 'snplay:cursor-pointer', !isPlaying && 'snplay:hidden')}
        />
      )}
      {!isPlaying && <img src={posterUrl} alt={label ?? ''} className={mediaClasses} />}
      {!isPlaying && videoUrl && (
        <button
          type="button"
          onClick={handlePlayClick}
          aria-label={label ? `Play ${label}` : 'Play video'}
          className={cn(
            'snplay-glass snplay:absolute snplay:top-1/2 snplay:left-1/2 snplay:flex',
            'snplay:h-20 snplay:w-20 snplay:-translate-x-1/2 snplay:-translate-y-1/2',
            'snplay:cursor-pointer snplay:items-center snplay:justify-center snplay:rounded-full',
            'snplay:text-ui-foreground snplay:shadow-lg',
          )}
        >
          <PlayIcon className="snplay:size-10" />
        </button>
      )}
      {!isPlaying && !videoUrl && (
        <div
          className={cn(
            'snplay-glass snplay:absolute snplay:top-1/2 snplay:left-1/2 snplay:flex',
            'snplay:h-20 snplay:w-20 snplay:-translate-x-1/2 snplay:-translate-y-1/2',
            'snplay:items-center snplay:justify-center snplay:rounded-full',
            'snplay:text-ui-foreground snplay:shadow-lg',
          )}
        >
          <PlayIcon className="snplay:size-10" />
        </div>
      )}
      {!isPlaying && posterOverlay}
    </div>
  )
}
